import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import './LinearProductRow.css'
import { useLinearProduct } from './useLinearProduct'

function parsePrice(value) {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : NaN
}

function FlyBubbles({ items }) {
  if (!items?.length) return null
  return createPortal(
    <>
      {items.map((fly) => (
        <div
          key={fly.id}
          className="linear-fly-bubble"
          style={{
            left: `${fly.fromX}px`,
            top: `${fly.fromY}px`,
            '--fly-dx': `${fly.dx}px`,
            '--fly-dy': `${fly.dy}px`,
          }}
          aria-hidden="true"
        >
          {fly.image ? (
            <img src={fly.image} alt="" className="linear-fly-bubble-image" />
          ) : (
            <span className="linear-fly-bubble-dot" />
          )}
        </div>
      ))}
    </>,
    document.body
  )
}

/**
 * Linear row: one product node on the spine. Reticle marker + hairline
 * connector in the spine column; HUD-framed panel (image + info) on its side.
 */
function LinearProductRow({ product, index = 0, side = 'left', cartCount = 0, onAddToCart }) {
  const {
    images,
    variants,
    selectedVariantId,
    selectVariant,
    selectedVariant,
    pricing,
    isAnimating,
    handleAddToCartClick,
    flyItems,
  } = useLinearProduct(product, { onAddToCart, cartCount })

  const numeral = String(index + 1).padStart(2, '0')
  const description = (product?.description ?? '').trim()

  const [imageIndex, setImageIndex] = useState(0)
  useEffect(() => {
    setImageIndex(0)
  }, [product?.id, product?._id])
  const safeImageIndex = images.length > 0 ? Math.min(imageIndex, images.length - 1) : 0
  const handlePrevImage = () => {
    setImageIndex((current) => (current - 1 + images.length) % images.length)
  }
  const handleNextImage = () => {
    setImageIndex((current) => (current + 1) % images.length)
  }

  return (
    <article
      className={`linear-row linear-row--${side}`}
      aria-label={product?.name ?? 'Товар'}
    >
      <div className="linear-row-marker" aria-hidden="true">
        <span className="linear-row-marker-index">{numeral}</span>
        <span className="linear-row-marker-reticle" />
      </div>
      <div className="linear-row-connector" aria-hidden="true" />

      <div className="linear-panel">
        <div className="linear-frame">
          {images.map((img, imgIndex) => (
            <img
              key={`${img.url}-${img.index}`}
              src={img.url}
              alt={imgIndex === safeImageIndex ? (product?.name ?? '') : ''}
              className={`linear-frame-image${imgIndex === safeImageIndex ? ' linear-frame-image--active' : ''}`}
              draggable={false}
            />
          ))}
          {images.length > 1 ? (
            <>
              <button
                type="button"
                className="linear-frame-nav linear-frame-nav--prev"
                onClick={handlePrevImage}
                aria-label="Предыдущее фото"
              >
                ‹
              </button>
              <button
                type="button"
                className="linear-frame-nav linear-frame-nav--next"
                onClick={handleNextImage}
                aria-label="Следующее фото"
              >
                ›
              </button>
              <span className="linear-frame-counter" aria-hidden="true">
                {`${String(safeImageIndex + 1).padStart(2, '0')} / ${String(images.length).padStart(2, '0')}`}
              </span>
            </>
          ) : null}
        </div>

        <div className="linear-panel-info">
          <h2 className="linear-title">{product?.name}</h2>

          {variants.length > 1 ? (
            <div className="linear-variants" role="listbox" aria-label="Варианты">
              {variants.map((variant) => {
                const variantId = variant.id ?? variant._id
                const selected = selectedVariantId === variantId
                const vPrice = parsePrice(variant.price)
                return (
                  <button
                    key={variantId}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`linear-variant${selected ? ' linear-variant--selected' : ''}`}
                    onClick={() => selectVariant(variantId)}
                  >
                    <span className="linear-variant-title">
                      {String(variant.title ?? '').trim() || 'Обычный'}
                    </span>
                    <span className="linear-variant-price">
                      {Number.isFinite(vPrice) ? `${vPrice.toFixed(2)}` : '—'}
                    </span>
                  </button>
                )
              })}
            </div>
          ) : null}

          <div className="linear-pricing">
            <span className="linear-price">
              {Number.isFinite(pricing.currentPrice)
                ? `${pricing.pricePrefix}${pricing.currentPrice.toFixed(2)} сом${pricing.priceSuffix || ''}`
                : '—'}
            </span>
            {pricing.oldPrice != null && (
              <span className="linear-price-old" aria-label="Прежняя цена">
                {pricing.oldPrice.toFixed(2)} сом
              </span>
            )}
          </div>

          <button
            type="button"
            className={`linear-add${isAnimating ? ' linear-add--animating' : ''}`}
            onClick={handleAddToCartClick}
            disabled={!selectedVariant}
          >
            В корзину
          </button>
        </div>
      </div>

      <div className="linear-aside">
        <div className="linear-meta">
          {`IDX-${numeral}`}
          {variants.length > 1 ? ` · ${variants.length} ВАРИАНТОВ` : ''}
        </div>
        {description ? (
          <p className="linear-description">{description}</p>
        ) : null}
      </div>

      <FlyBubbles items={flyItems} />
    </article>
  )
}

export default LinearProductRow
